// Campaign fields come back in different shapes depending on the endpoint:
// browse/search aggregations project raw/unpopulated values, while the
// campaign-groups endpoints return real Mongoose-populated docs. These
// helpers normalize both so any component can read a plain value/array.

export function getCategoryLabel(campaign) {
    if (typeof campaign?.category === 'string') return campaign.category;
    // Other pre-existing call sites in this app read `.name` off a
    // populated category ref rather than `.label` — support both so this
    // doesn't go blank depending on which one a given document actually has.
    return campaign?.category?.[0]?.name || campaign?.category?.[0]?.label;
}

export function getPlatformNames(campaign) {
    if (typeof campaign?.platform === 'string') {
        return campaign.platform ? [campaign.platform] : [];
    }
    return (campaign?.platform || []).map((p) => p.name).filter(Boolean);
}

export function getCreatedByName(campaign) {
    const raw = campaign?.createdby;
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (typeof value === 'string' && value) return value;
    if (typeof value?.fullName === 'string' && value.fullName) return value.fullName;
    // Anything else (a populated doc missing fullName, null, etc.) would be
    // rendered directly as a React child by callers — never return it as-is.
    return 'Unknown';
}

// Unlike getCreatedByName, a campaign legitimately has no frozenBy at all
// (it's simply not frozen) — so this returns null rather than 'Unknown' in
// that case, letting callers tell "not frozen" apart from "frozen but the
// freezer's name is missing".
export function getFrozenByName(campaign) {
    const raw = campaign?.frozenBy;
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (typeof value === 'string' && value) return value;
    if (typeof value?.fullName === 'string' && value.fullName) return value.fullName;
    return null;
}

// Maps a raw campaign document from the API (the details fetch, or the
// updated doc a freeze/revenue/budget endpoint returns) onto the flattened
// shape the campaign details page keeps. `fallback` is the current value —
// any field the doc doesn't carry, or carries only as an unpopulated
// ObjectId (e.g. category on POST campaign/id, createdby/frozenBy on update
// responses), keeps its fallback instead of being blanked or replaced by an
// id string. Used for both the initial load and later patches so there's
// one definition of this shape.
export function normalizeCampaignDetails(doc, fallback = {}) {
    const pick = (key) => (doc[key] !== undefined ? doc[key] : fallback[key]);
    const platformNames = getPlatformNames(doc).join(', ');
    let frozenBy = doc.frozenBy?.fullName;
    if (frozenBy === undefined) {
        frozenBy = doc.isFrozen === false ? null : (fallback.frozenBy ?? null);
    }

    // POST campaign/id returns revenueAmount (super-admin only) but not the
    // derived netRevenue the list endpoints add, so derive it here with the
    // backend's formula (common/revenue.js withNetRevenue: revenue - budget).
    // Recomputed from the resolved values so a budget/revenue patch updates it.
    const totalPayment = pick('totalPayment');
    const revenueAmount = pick('revenueAmount');
    let netRevenue = null;
    if (doc.netRevenue !== undefined) {
        netRevenue = doc.netRevenue;
    } else if (revenueAmount != null) {
        netRevenue = Number(revenueAmount) - parseStoredAmount(totalPayment);
    }

    return {
        id: doc._id || doc.id || fallback.id,
        name: pick('name'),
        category: getCategoryLabel(doc) || fallback.category,
        deliverables: pick('deliverables'),
        platform: platformNames || fallback.platform,
        totalPayment,
        paymentLimit: pick('paymentLimit'),
        createdby: doc.createdby?.fullName || fallback.createdby,
        isOfficeExpense: pick('isOfficeExpense'),
        isFrozen: pick('isFrozen'),
        frozenBy,
        revenueAmount,
        revenueLockedDate: pick('revenueLockedDate'),
        netRevenue,
    };
}

// Some legacy rows have "NaN" literally saved as a totalPayment/paymentLimit
// value (from an older buggy write path) rather than a real amount or a
// missing value — shown as-is that reads as "₹NaN" wherever it's displayed,
// so it's swapped for a plain 0 here. Anything else (a real number, or a
// legit comma-grouped string like "1,50,000") passes through untouched —
// in particular, this must NOT run the value through Number(), since
// Number("1,50,000") is itself NaN.
export const safeAmount = (value) => (value === 'NaN' || value == null ? 0 : value);

// For call sites that need the *numeric* current amount (arithmetic, not
// display) — unlike safeAmount above, which deliberately leaves a legit
// comma-grouped string like "1,50,000" untouched for rendering. Mirrors the
// backend's routes/campaign.js parseStoredAmount: strips grouping commas
// before parsing, so a legacy comma-formatted value doesn't collapse to
// NaN/0 here the way a bare Number() on it would.
export const parseStoredAmount = (value) => {
    const n = Number(String(value ?? '').replace(/,/g, ''));
    return Number.isNaN(n) ? 0 : n;
};

export const uniqueValues = (values) => Array.from(new Set(values.filter(Boolean)));

// Rolls a group's member campaigns up into the totals GroupCard displays (and
// CampaignSort sorts groups by) — shared so the two can never drift on what
// "a group's budget/limit/net revenue" means. A campaign deleted elsewhere
// stays referenced in the group doc (deleting doesn't pull it out of every
// group it's in), so isDeleted ones are excluded here the same way the group
// drill-down view does.
//
// totalNetRevenue is null (hidden, same as a single campaign's own
// CampaignCard) unless at least one member actually has netRevenue set —
// the backend (routes/campaignGroups.js) only puts it on a campaign at all
// once its revenue has been locked, and only for a super-admin to begin
// with, so most members most of the time won't have it. Members that do
// are summed; members that don't contribute 0, same as a group with no
// revenue-locked members at all contributing nothing rather than skewing
// the total toward whichever few happen to be locked.
export function computeGroupTotals(group) {
    let totalBudget = 0;
    let totalLimit = 0;
    let totalNetRevenue = 0;
    let hasNetRevenue = false;
    (group.campaigns || [])
        .filter((c) => !c.isDeleted)
        .forEach((member) => {
            totalBudget += Number(member.totalPayment) || 0;
            totalLimit += Number(member.paymentLimit) || 0;
            if (member.netRevenue != null) {
                hasNetRevenue = true;
                totalNetRevenue += Number(member.netRevenue) || 0;
            }
        });
    return { totalBudget, totalLimit, totalNetRevenue: hasNetRevenue ? totalNetRevenue : null };
}

export const initials = (name) => {
    const str = typeof name === 'string' ? name : '?';
    return str
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('');
};
